import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { brokerListQueryOptions, pvRangeQueryOptions } from '@/features/brokers/api/queries'
import { rememberLastBrokerStock } from '@/features/brokers/lib/lastUsed'
import type { RouterContext } from '@/routes/layouts/root'
import { PV_RANGE_BROKER_LIST_QUERY } from '@/screens/brokers/lib/pvRangeBrokerList'
import { pvRangeSearchSchema, toPVRangeQuery } from '@/screens/brokers/search'

/**
 * The `/brokers/pv-range/$symbol` loader.
 *
 * MAIN, but keyed on the `period`/`buyer`/`seller` search params
 * (`pvRangeSearchSchema`), so this awaits only the ENTRY URL's query — a
 * filter or period change afterwards is search-only and reruns no loader,
 * and `PVRangeScreen` keeps reading it with `usePVRange`'s own
 * `placeholderData: keepPreviousData`, not a suspense hook
 * (docs/route-loading.md, "Search params").
 *
 * SECONDARY, started and not awaited (and only on a real navigation — see
 * docs/route-loading.md, "Secondary reads"): the broker list the two side
 * filters populate their menus from — it needs nothing from the profile, and
 * the filters draw their own `isLoadingChoices` state while it is still out.
 * Skipped on a hover-intent `preload` so a cursor merely crossing the sidebar
 * row does not fire it; the MAIN read below still starts on hover.
 *
 * `STOCK_NOT_FOUND`/`NO_FLOORSHEET_DATA` resolve to `'not-found'`/
 * `'no-floorsheet'` rather than throwing (docs/route-loading.md, "Not-found
 * answers" — see `pvRangeQueryOptions`'s own comment), so this loader settles
 * normally either way and `PVRangeScreen` keeps its own branch for both.
 * Remembers the symbol as last-used only for an outcome that names a REAL
 * stock — `'not-found'` is not a visit worth remembering, on the SAME key
 * `ByStockScreen.loader.ts` writes (the two screens share one "last
 * broker-analysis stock", `lastUsed.ts`) — and only for a real navigation
 * (`!preload`): the router runs this loader on a hover too, and a member's
 * cursor merely crossing a stock link must not overwrite what they last
 * actually opened.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own: typed inside `createFileRoute`, a loader is bound to this
 * route's parent in the generated tree. It imports from `api/`, `lib/` and
 * schema modules only — route loaders are not code-split, so anything
 * imported here ships in the entry chunk.
 */
export async function loadPVRange({
  context: { queryClient },
  params,
  location,
  preload,
}: {
  context: RouterContext
  params: { symbol: string }
  location: { search: unknown }
  preload: boolean
}): Promise<void> {
  if (!preload) prefetchRouteData(queryClient, brokerListQueryOptions(PV_RANGE_BROKER_LIST_QUERY))
  const search = pvRangeSearchSchema.parse(location.search)
  const result = await ensureRouteData(
    queryClient,
    pvRangeQueryOptions(params.symbol, toPVRangeQuery(search)),
  )
  if (!preload && result !== 'not-found') rememberLastBrokerStock(params.symbol)
}
